/**
 * useLiveMatchPlayers
 *
 * Fetches the playing roster for each participant when Advanced Scoring mode
 * is activated.
 *
 * - For "team" participants  → fetches active team members via GET /teams/:id/members
 * - For "user" participants  → wraps the single user from match data in an array
 *
 * Returns:
 *   {
 *     p1Players:      Array<PlayerObject>    — participant 1 roster
 *     p2Players:      Array<PlayerObject>    — participant 2 roster
 *     loadingPlayers: boolean
 *     playersError:   string|null
 *     refreshPlayers: () => void
 *   }
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '../../../../services/api/apiService';

const useLiveMatchPlayers = (match, advancedMode) => {
  const [p1Players, setP1Players] = useState([]);
  const [p2Players, setP2Players] = useState([]);
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [playersError, setPlayersError] = useState(null);
  // Prevent duplicate fetches for the same match
  const fetchedForRef = useRef(null);

  const fetchTeamMembers = useCallback(async (teamId) => {
    const response = await apiClient.get(`/teams/${teamId}/members`);
    // API response shape: { success, data: { members: [], team, pagination, ... } }
    const raw = response?.data;
    let list = null;

    if (Array.isArray(raw))                       list = raw;              // bare array
    else if (Array.isArray(raw?.data?.members))   list = raw.data.members; // ← real shape
    else if (Array.isArray(raw?.members))         list = raw.members;
    else if (Array.isArray(raw?.data))            list = raw.data;

    if (!list) return [];

    // Normalise to a consistent shape the modal expects
    return list.map((m) => ({
      id:           m.user_id ?? m.id,
      name:         [m.first_name, m.last_name].filter(Boolean).join(' ') || m.username || 'Player',
      username:     m.username,
      avatar_url:   m.profile_picture || m.avatar_url || null,
      position:     m.position || null,
      jerseyNumber: m.jersey_number ?? null,
      role:         m.role || m.member_role || null,
      status:       m.status || null,
      memberType:   m.member_type || 'primary',
    }));
  }, []);

  const buildUserPlayer = useCallback((match, participantIndex) => {
    // participantIndex: 1 or 2
    const idKey  = `participant${participantIndex}Id`  || (participantIndex === 1 ? 'participant_1_id'   : 'participant_2_id');
    const nameKey = participantIndex === 1
      ? (match.participant1Name || match.participant_1_name || null)
      : (match.participant2Name || match.participant_2_name || null);
    const userId = match[idKey] || match[`participant_${participantIndex}_id`];

    return [{
      id:         userId,
      name:       nameKey  || `Player ${participantIndex}`,
      position:   null,
      jerseyNumber: null,
      type:       'user',
    }];
  }, []);

  const fetchPlayers = useCallback(async () => {
    if (!match) return;
    const matchId = match.id || match.matchId;
    if (fetchedForRef.current === matchId) return;

    // Short-circuit for drop-in matches — roster is already in match_config
    const dit = (match.matchConfig || match.match_config)?.drop_in_teams;
    if (dit) {
      const normalise = (players) =>
        (players || []).map((p) => ({
          id:           p.user_id ?? p.id,
          name:         p.display_name || [p.first_name, p.last_name].filter(Boolean).join(' ') || p.username || 'Player',
          username:     p.username,
          avatar_url:   p.avatar_url || p.profile_picture || null,
          position:     p.position || null,
          jerseyNumber: p.jersey_number ?? null,
          type:         'user',
          memberType:   p.member_type || 'primary',
        }));
      setP1Players(normalise(dit.team_a?.players));
      setP2Players(normalise(dit.team_b?.players));
      fetchedForRef.current = matchId;
      return;
    }

    setLoadingPlayers(true);
    setPlayersError(null);

    try {
      const p1Type = match.participant1Type || match.participant_1_type || 'user';
      const p2Type = match.participant2Type || match.participant_2_type || 'user';
      const p1Id   = match.participant1Id   || match.participant_1_id;
      const p2Id   = match.participant2Id   || match.participant_2_id;

      const [roster1, roster2] = await Promise.all([
        p1Type === 'team' ? fetchTeamMembers(p1Id) : Promise.resolve(buildUserPlayer(match, 1)),
        p2Type === 'team' ? fetchTeamMembers(p2Id) : Promise.resolve(buildUserPlayer(match, 2)),
      ]);

      setP1Players(roster1);
      setP2Players(roster2);
      fetchedForRef.current = matchId;
    } catch (err) {
      console.error('[useLiveMatchPlayers] Failed to fetch rosters:', err);
      setPlayersError('Could not load player roster. Some advanced scoring features may be unavailable.');
    } finally {
      setLoadingPlayers(false);
    }
  }, [match, fetchTeamMembers, buildUserPlayer]);

  useEffect(() => {
    if (advancedMode && match) {
      fetchPlayers();
    }
  }, [advancedMode, match, fetchPlayers]);

  // Reset when match changes
  useEffect(() => {
    if (!match) {
      setP1Players([]);
      setP2Players([]);
      fetchedForRef.current = null;
    }
  }, [match]);

  const refreshPlayers = useCallback(() => {
    if (!match) return;
    fetchedForRef.current = null; // clear cache so fetchPlayers re-runs
    fetchPlayers();
  }, [match, fetchPlayers]);

  return { p1Players, p2Players, loadingPlayers, playersError, refreshPlayers };
};

export default useLiveMatchPlayers;
